import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BadgeCheck, Undo2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { useLang } from "../i18n";
import PhotoManager from "../components/PhotoManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CATS = ["cars", "vans", "trucks", "trailers", "forklifts"];
const STATUSES = ["available", "reserved", "sold"];

const STATUS_BADGE = {
  available: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  reserved: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  sold: "bg-red-500/15 text-red-400 border-red-500/30",
};

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/") ? `${BE}${u}` : u);

const EMPTY = {
  title: "", brand: "", category: "cars", price: "", year: "", mileage: "",
  gearbox: "", drivetrain: "", fuel: "", engine: "", power: "", emission: "", color: "",
  images: [], status: "available", featured: false, offer: false, description: "",
};

const inputCls =
  "w-full bg-[#0D0F16] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[rgba(68,153,247,0.5)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono-num tracking-widest uppercase text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function AdminVehicles() {
  const { t } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (v) => {
    setForm({
      title: v.title, brand: v.brand, category: v.category,
      price: String(v.price), year: String(v.year), mileage: String(v.mileage),
      gearbox: v.gearbox || "", drivetrain: v.drivetrain || "", fuel: v.fuel || "", engine: v.engine || "", power: v.power || "",
      emission: v.emission || "", color: v.color || "",
      images: v.images && v.images.length ? [...v.images] : [v.image],
      status: v.status, featured: !!v.featured, offer: !!v.offer, description: v.description || "",
    });
    setEditing(v.id);
  };

  const payloadFrom = () => ({
    title: form.title.trim(),
    brand: form.brand.trim(),
    category: form.category,
    price: Number(form.price),
    year: Number(form.year),
    mileage: Number(form.mileage) || 0,
    gearbox: form.gearbox.trim(),
    drivetrain: (form.drivetrain || "").trim(),
    fuel: form.fuel.trim(),
    engine: (form.engine || "").trim(),
    power: form.power.trim(),
    emission: form.emission.trim(),
    color: form.color.trim(),
    image: form.images[0] || "",
    images: form.images,
    status: form.status,
    featured: form.featured,
    offer: form.offer,
    description: (form.description || "").trim(),
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) {
      toast.error(t("admin.photos"));
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/vehicles", payloadFrom());
      } else {
        await api.put(`/vehicles/${editing}`, payloadFrom());
      }
      toast.success(t("admin.savedVehicle"));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(t("admin.deleteConfirm"))) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success(t("admin.deletedVehicle"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSold = async (v) => {
    const next = v.status === "sold" ? "available" : "sold";
    try {
      await api.put(`/vehicles/${v.id}`, {
        title: v.title, brand: v.brand, category: v.category,
        price: v.price, year: v.year, mileage: v.mileage,
        gearbox: v.gearbox || "", drivetrain: v.drivetrain || "", fuel: v.fuel || "", engine: v.engine || "", power: v.power || "",
        emission: v.emission || "", color: v.color || "",
        image: v.image, images: v.images || [v.image],
        status: next, featured: !!v.featured, offer: !!v.offer, description: v.description || "",
      });
      toast.success(t("admin.savedVehicle"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-vehicles-panel">
      <div className="flex items-center justify-between gap-4 mt-10">
        <p className="font-mono-num text-xs tracking-[0.25em] uppercase text-slate-500">{vehicles.length} {t("inv.results")}</p>
        <button
          data-testid="admin-add-vehicle-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#6FB1FA] transition-colors"
        >
          <Plus size={16} />
          {t("admin.addVehicle")}
        </button>
      </div>

      <div className="mt-6 bg-[#12141D] border border-white/10 rounded-3xl overflow-hidden" data-testid="admin-vehicles-table">
        {vehicles.map((v) => (
          <div
            key={v.id}
            data-testid={`admin-vehicle-row-${v.id}`}
            className="flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <img src={imgSrc(v.image)} alt="" className="w-20 h-14 object-cover rounded-lg border border-white/10 shrink-0" loading="lazy" />
              <div className="min-w-0">
                <p className="font-semibold text-slate-100 truncate">{v.title}</p>
                <p className="text-xs text-slate-500 font-mono-num">{v.brand} · {v.year} · €{v.price.toLocaleString("en-US").replace(/,/g, " ")}</p>
              </div>
            </div>
            <span
              data-testid={`admin-vehicle-status-${v.id}`}
              className={`self-start text-[11px] font-mono-num tracking-widest uppercase border rounded-full px-3 py-1 ${STATUS_BADGE[v.status] || STATUS_BADGE.available}`}
            >
              {t(`status.${v.status}`)}
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid={`admin-vehicle-toggle-sold-${v.id}`}
                onClick={() => toggleSold(v)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-4 py-2"
              >
                {v.status === "sold" ? <Undo2 size={13} /> : <BadgeCheck size={13} />}
                {v.status === "sold" ? t("admin.markAvailable") : t("admin.markSold")}
              </button>
              <button
                data-testid={`admin-vehicle-edit-${v.id}`}
                onClick={() => openEdit(v)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-4 py-2"
              >
                <Pencil size={13} />
                {t("admin.editVehicle")}
              </button>
              <button
                data-testid={`admin-vehicle-delete-${v.id}`}
                onClick={() => remove(v)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors border border-white/10 rounded-full px-4 py-2"
              >
                <Trash2 size={13} />
                {t("admin.deleteVehicle")}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent
          className="bg-[#12141D] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="vehicle-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-50">
              {editing === "new" ? t("admin.addVehicle") : t("admin.editVehicle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <Field label={t("admin.fTitle")}>
                <input data-testid="vehicle-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
              </Field>
            </div>
            <Field label={t("admin.fBrand")}>
              <input data-testid="vehicle-form-brand" required value={form.brand} onChange={set("brand")} className={inputCls} />
            </Field>
            <Field label={t("admin.fCategory")}>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="vehicle-form-category" className="bg-[#0D0F16] border-white/10 rounded-xl text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-slate-200">
                  {CATS.map((c) => (
                    <SelectItem key={c} value={c}>{t(`cats.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("admin.fPrice")}>
              <input data-testid="vehicle-form-price" required type="number" min="0" step="1" value={form.price} onChange={set("price")} className={inputCls} />
            </Field>
            <Field label={t("admin.fYear")}>
              <input data-testid="vehicle-form-year" required type="number" min="1950" max="2030" value={form.year} onChange={set("year")} className={inputCls} />
            </Field>
            <Field label={t("admin.fMileage")}>
              <input data-testid="vehicle-form-mileage" type="number" min="0" value={form.mileage} onChange={set("mileage")} className={inputCls} />
            </Field>
            <Field label={t("admin.fGearbox")}>
              <input data-testid="vehicle-form-gearbox" value={form.gearbox} onChange={set("gearbox")} className={inputCls} />
            </Field>
            <Field label={t("admin.fDrivetrain")}>
              <input data-testid="vehicle-form-drivetrain" value={form.drivetrain} onChange={set("drivetrain")} className={inputCls} />
            </Field>
            <Field label={t("admin.fFuel")}>
              <input data-testid="vehicle-form-fuel" value={form.fuel} onChange={set("fuel")} className={inputCls} />
            </Field>
            <Field label={t("admin.fEngine")}>
              <input data-testid="vehicle-form-engine" value={form.engine} onChange={set("engine")} className={inputCls} />
            </Field>
            <Field label={t("admin.fPower")}>
              <input data-testid="vehicle-form-power" value={form.power} onChange={set("power")} className={inputCls} />
            </Field>
            <Field label={t("admin.fEmission")}>
              <input data-testid="vehicle-form-emission" value={form.emission} onChange={set("emission")} className={inputCls} />
            </Field>
            <Field label={t("admin.fColor")}>
              <input data-testid="vehicle-form-color" value={form.color} onChange={set("color")} className={inputCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("admin.fDescription")}>
                <textarea
                  data-testid="vehicle-form-description"
                  rows={5}
                  value={form.description}
                  onChange={set("description")}
                  className={`${inputCls} resize-y`}
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <PhotoManager
                images={form.images}
                onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))}
                onUploadingChange={setUploading}
              />
            </div>

            <Field label={t("admin.fStatus")}>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="vehicle-form-status" className="bg-[#0D0F16] border-white/10 rounded-xl text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-slate-200">
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-3 pt-6 cursor-pointer">
              <input
                data-testid="vehicle-form-featured"
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                className="w-4 h-4 accent-[#4499F7]"
              />
              <span className="text-sm text-slate-300">{t("admin.fFeatured")}</span>
            </label>
            <label className="flex items-center gap-3 pt-6 cursor-pointer">
              <input
                data-testid="vehicle-form-offer"
                type="checkbox"
                checked={form.offer}
                onChange={(e) => setForm((f) => ({ ...f, offer: e.target.checked }))}
                className="w-4 h-4 accent-amber-400"
              />
              <span className="text-sm text-slate-300">{t("admin.fOffer")}</span>
            </label>
            <div className="sm:col-span-2 flex gap-3 pt-2">
              <button
                data-testid="vehicle-form-save-btn"
                type="submit"
                disabled={saving || uploading || !form.images.length}
                className="bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-8 py-3 hover:bg-[#6FB1FA] disabled:opacity-60 transition-colors"
              >
                {saving ? t("admin.saving") : t("admin.save")}
              </button>
              <button
                data-testid="vehicle-form-cancel-btn"
                type="button"
                onClick={() => setEditing(null)}
                className="border border-white/15 text-slate-300 text-sm rounded-full px-8 py-3 hover:border-[rgba(68,153,247,0.5)] hover:text-[#4499F7] transition-colors"
              >
                {t("admin.cancel")}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
